import { useState, useMemo, useEffect } from "react";
import { useGetStatusQuery, useGetTradesQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { fmtNum, fmtTime, fmtUsd } from "@/lib/format";
import { PnLPill } from "@/components/domain/PnLPill";
import { PriceChart } from "@/components/charts/PriceChart";
import { cn } from "@/lib/utils";

const TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "1d"] as const;

export default function Markets() {
  const status = useGetStatusQuery(undefined, { pollingInterval: 10000 });
  const trades = useGetTradesQuery(undefined, { pollingInterval: 30000 });

  const markets = status.data?.markets ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<(typeof TIMEFRAMES)[number]>("1m");

  // Default the selection to the first market once data lands.
  useEffect(() => {
    if (selected == null && markets.length > 0) {
      setSelected(markets[0].symbol);
    }
  }, [markets, selected]);

  const sel = useMemo(
    () => markets.find((m) => m.symbol === selected) ?? markets[0],
    [markets, selected],
  );

  const symbolTrades = useMemo(
    () => (trades.data ?? []).filter((t) => sel && t.symbol === sel.symbol).slice(0, 25),
    [trades.data, sel],
  );

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-4">
      <Card className="lg:sticky lg:top-4 self-start">
        <CardHeader>
          <CardTitle>Whitelist</CardTitle>
        </CardHeader>
        <CardContent>
          {status.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : markets.length === 0 ? (
            <p className="text-sm text-muted">No symbols configured.</p>
          ) : (
            <ul className="space-y-1">
              {markets.map((m) => {
                const active = sel?.symbol === m.symbol;
                const changeCls =
                  m.change_pct_24h > 0
                    ? "text-good"
                    : m.change_pct_24h < 0
                    ? "text-bad"
                    : "text-muted";
                return (
                  <li key={m.symbol}>
                    <button
                      onClick={() => setSelected(m.symbol)}
                      className={cn(
                        "w-full flex items-center justify-between rounded-md px-2.5 h-9 text-sm hover:bg-bg",
                        active && "bg-bg",
                      )}
                    >
                      <span className="font-medium">{m.symbol}</span>
                      <span className={cn("tabular text-xs", changeCls)}>
                        {(m.change_pct_24h >= 0 ? "+" : "") + m.change_pct_24h.toFixed(2)}%
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4 min-w-0">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 flex-wrap">
            <CardTitle>{sel?.symbol ?? "—"}</CardTitle>
            <div className="flex items-center gap-1">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  className={cn(
                    "px-2 h-7 rounded-md text-xs border transition-colors",
                    timeframe === tf
                      ? "bg-bg text-fg border-border"
                      : "text-muted border-transparent hover:text-fg hover:bg-bg",
                  )}
                >
                  {tf}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {sel && (
              <PriceChart symbol={sel.symbol} timeframe={timeframe} limit={240} heightPx={360} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Snapshot stats</CardTitle>
          </CardHeader>
          <CardContent>
            {!sel ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <Stat label="Last price" value={fmtNum(sel.price, 4)} />
                <Stat
                  label="24h change"
                  value={(sel.change_pct_24h >= 0 ? "+" : "") + sel.change_pct_24h.toFixed(2) + "%"}
                  tone={sel.change_pct_24h > 0 ? "good" : sel.change_pct_24h < 0 ? "bad" : "muted"}
                />
                <Stat label="RSI(14) 1h" value={sel.rsi_14.toFixed(1)} />
                <Stat label="SMA20 1h" value={fmtNum(sel.sma_20, 4)} />
                <Stat label="SMA50 1h" value={fmtNum(sel.sma_50, 4)} />
                <Stat
                  label="vs SMA20"
                  value={
                    sel.sma_20 > 0
                      ? `${(((sel.price - sel.sma_20) / sel.sma_20) * 100).toFixed(2)}%`
                      : "-"
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              Recent trades on {sel?.symbol ?? "this pair"}
              <span className="text-xs font-normal text-muted ml-2">last 25</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trades.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : symbolTrades.length === 0 ? (
              <p className="text-sm text-muted">No trades on this pair yet.</p>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Time</TH>
                    <TH>Side</TH>
                    <TH className="text-right">Size USD</TH>
                    <TH className="text-right">Avg price</TH>
                    <TH className="text-right">Fee</TH>
                    <TH>Source</TH>
                  </TR>
                </THead>
                <TBody>
                  {symbolTrades.map((t) => (
                    <TR key={t.id}>
                      <TD className="text-xs text-muted">{fmtTime(t.executed_at)}</TD>
                      <TD>
                        <Badge tone={t.action === "BUY" ? "good" : "bad"}>{t.action}</Badge>
                      </TD>
                      <TD className="text-right tabular">{fmtUsd(t.size_usd ?? null)}</TD>
                      <TD className="text-right tabular">{fmtNum(t.avg_price ?? null, 6)}</TD>
                      <TD className="text-right">
                        <PnLPill value={-(t.fee ?? 0)} fractionDigits={4} />
                      </TD>
                      <TD>
                        <Badge tone={t.source === "scalp" ? "info" : "default"}>
                          {t.source ?? "hourly"}
                        </Badge>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad" | "muted";
}) {
  const cls =
    tone === "good" ? "text-good" : tone === "bad" ? "text-bad" : tone === "muted" ? "text-muted" : "";
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className={cn("tabular text-lg font-semibold", cls)}>{value}</div>
    </div>
  );
}
